import { api } from './client';
import { resetCsrfToken } from './csrf';
import { isApiError } from './errors';
import { adminSessionSchema, type AdminSession, type LoginRequest } from './types';

/** The signed-in admin, or null when there is no session. */
export async function fetchSession(signal?: AbortSignal): Promise<AdminSession | null> {
  try {
    const body = await api.get<unknown>('/auth/me', {
      skipUnauthorizedHandler: true,
      ...(signal ? { signal } : {}),
    });
    return adminSessionSchema.parse(body);
  } catch (error) {
    if (isApiError(error) && error.isUnauthorized) return null;
    throw error;
  }
}

export async function login(credentials: LoginRequest): Promise<AdminSession> {
  // A token issued before sign-in is not valid after it: the server rotates it.
  resetCsrfToken();
  try {
    await api.post<unknown>('/auth/login', credentials, { skipUnauthorizedHandler: true });
  } finally {
    resetCsrfToken();
  }
  const session = await fetchSession();
  if (!session) throw new Error('Signed in, but the session could not be read.');
  return session;
}

export async function logout(): Promise<void> {
  try {
    await api.post<unknown>('/auth/logout', undefined, { skipUnauthorizedHandler: true });
  } finally {
    resetCsrfToken();
  }
}
